import { useState } from 'react';
import { Button } from '@/components/ui/PageHeader';
import { InputField, TextAreaField, SelectField, TagsField, CheckboxField } from '@/components/ui/FormFields';
import { FileUploadField } from '@/components/ui/FileUploadField';
import {
  createBoard,
  updateBoard,
  type NostalgiaBoard,
  type CreateBoardInput,
  ERA_OPTIONS,
  THEME_OPTIONS,
  LAYOUT_OPTIONS,
  VISIBILITY_OPTIONS,
} from '@/api/nostalgia';
import type { UploadResult } from '@/api/upload';

interface BoardFormProps {
  board?: NostalgiaBoard;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function BoardForm({ board, onSuccess, onCancel }: BoardFormProps) {
  const isEditing = !!board;

  const [formData, setFormData] = useState<CreateBoardInput>({
    title: board?.title || '',
    description: board?.description || '',
    era: board?.era || undefined,
    theme: board?.theme || undefined,
    region: board?.region || '',
    tags: board?.tags || [],
    coverImageUrl: board?.coverImageUrl || '',
    layoutMode: board?.layoutMode || 'grid',
    visibility: board?.visibility || 'everyone',
    isCollaborative: board?.isCollaborative || false,
    backgroundColor: board?.backgroundColor || '',
    backgroundImageUrl: board?.backgroundImageUrl || '',
  });
  const [coverUploadResult, setCoverUploadResult] = useState<UploadResult | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Clean up empty strings
      const cleanData: CreateBoardInput = {
        ...formData,
        description: formData.description || undefined,
        region: formData.region || undefined,
        coverImageUrl: formData.coverImageUrl || undefined,
        backgroundColor: formData.backgroundColor || undefined,
        backgroundImageUrl: formData.backgroundImageUrl || undefined,
      };

      if (isEditing) {
        await updateBoard(board.id, cleanData);
      } else {
        await createBoard(cleanData);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save board');
    } finally {
      setIsLoading(false);
    }
  };

  const updateField = <K extends keyof CreateBoardInput>(field: K, value: CreateBoardInput[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">{error}</div>
      )}

      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 border-b pb-2">Basic Info</h3>

        <InputField
          label="Title"
          value={formData.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="My 90s Childhood Memories"
          required
        />

        <TextAreaField
          label="Description"
          value={formData.description || ''}
          onChange={(e) => updateField('description', e.target.value)}
          placeholder="A collection of memories from growing up in the 90s..."
          rows={3}
        />

        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Era"
            value={formData.era || ''}
            onChange={(e) => updateField('era', e.target.value as CreateBoardInput['era'])}
            options={ERA_OPTIONS}
            placeholder="Select Era"
          />

          <SelectField
            label="Theme"
            value={formData.theme || ''}
            onChange={(e) => updateField('theme', e.target.value as CreateBoardInput['theme'])}
            options={THEME_OPTIONS}
            placeholder="Select Theme"
          />
        </div>

        <InputField
          label="Region"
          value={formData.region || ''}
          onChange={(e) => updateField('region', e.target.value)}
          placeholder="Mumbai, Delhi, Chennai..."
        />

        <TagsField
          label="Tags"
          value={formData.tags || []}
          onChange={(tags) => updateField('tags', tags)}
          placeholder="doordarshan, childhood, 90s..."
          hint="Press Enter to add tags"
        />
      </div>

      {/* Layout & Display */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 border-b pb-2">Layout & Display</h3>

        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Layout Mode"
            value={formData.layoutMode || 'grid'}
            onChange={(e) => updateField('layoutMode', e.target.value as CreateBoardInput['layoutMode'])}
            options={LAYOUT_OPTIONS}
          />

          <SelectField
            label="Visibility"
            value={formData.visibility || 'everyone'}
            onChange={(e) => updateField('visibility', e.target.value as CreateBoardInput['visibility'])}
            options={VISIBILITY_OPTIONS}
          />
        </div>

        <FileUploadField
          label="Cover Image"
          mediaType="image"
          value={coverUploadResult}
          onChange={(result) => {
            setCoverUploadResult(result);
            if (result) {
              updateField('coverImageUrl', result.url);
            } else {
              updateField('coverImageUrl', '');
            }
          }}
          urlValue={formData.coverImageUrl || ''}
          onUrlChange={(url) => updateField('coverImageUrl', url)}
        />

        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="Background Color"
            value={formData.backgroundColor || ''}
            onChange={(e) => updateField('backgroundColor', e.target.value)}
            placeholder="#FF6B35 or rgb(...)"
          />

          <InputField
            label="Background Image URL"
            type="url"
            value={formData.backgroundImageUrl || ''}
            onChange={(e) => updateField('backgroundImageUrl', e.target.value)}
            placeholder="https://..."
          />
        </div>

        <CheckboxField
          label="Collaborative board (allow others to add items)"
          checked={formData.isCollaborative || false}
          onChange={(e) => updateField('isCollaborative', e.target.checked)}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {isEditing ? 'Update Board' : 'Create Board'}
        </Button>
      </div>
    </form>
  );
}
